/**
 * Responder API Hooks
 *
 * TanStack Query hooks for responder-related operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { responderService } from '@/api/services/responderService'
import { MUTATION_KEYS } from '@/api'
import {
  NotifyResponderRequest,
  ResponderListParams,
} from '@/types/api/responders'
import { createApiRetryHandler } from '@/utils/apiRetry'

/**
 * Hook to get a single responder by ID
 */
export const useResponderById = (responderId: string) => {
  return useQuery({
    queryKey: [MUTATION_KEYS.RESPONDERS, 'detail', responderId],
    queryFn: () => responderService.getResponderById(responderId),
    enabled: !!responderId,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: createApiRetryHandler(3),
    refetchOnWindowFocus: false,
  })
}

/**
 * Hook to get all responders
 */
export const useAllResponders = (params: ResponderListParams) => {
  return useQuery({
    queryKey: [MUTATION_KEYS.RESPONDERS, params],
    queryFn: () => responderService.getAllResponders(params),
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    retry: createApiRetryHandler(3),
  })
}

// MUTATION HOOKS

/**
 * Mutation hook to notify responders
 */
export const useNotifyResponder = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: [MUTATION_KEYS.NOTIFY_RESPONDER],
    mutationFn: (data: NotifyResponderRequest) =>
      responderService.notifyResponder(data),
    onSuccess: (_, { caseId }) => {
      // Invalidate case responders to show updated status
      queryClient.invalidateQueries({
        queryKey: ['caseResponders', caseId],
      })

      toast.success('Responders notified successfully')
    },
    onError: (error: any) => {
      console.error('Failed to notify responders:', error)
      toast.error(error.message || 'Failed to notify responders')
    },
  })
}
